/**
 * 生成结果落库入口（T11b）。
 *
 * `saveGeneratedImages()` 把生图适配器产出的 `GeneratedImage[]` 批量写入图库：
 * - 同一批内按 dataUrl 去重；已存在于图库的相同 dataUrl 也不再重复入库；
 * - dataUrl 估算体积超过阈值（默认 2MB）时不静默失败，而是跳过并返回明确提示。
 * 生图适配器契约见 `src/core/image/types.ts` 的 `GeneratedImage`。
 */
import type { GeneratedImage } from '../../core/image';
import { getDb } from '../../core/db';
import {
  createAndSaveGalleryImage,
  type GalleryImage,
} from './galleryRepo';

/** 单张图片 dataUrl 的体积上限（估算值，超过则跳过并提示）。 */
export const DEFAULT_MAX_DATA_URL_BYTES = 2 * 1024 * 1024;

export interface SaveGeneratedImagesOptions {
  /** 生图提示词，写入每条图库记录。 */
  prompt: string;
  /** 使用的模型标识，写入每条图库记录。 */
  model: string;
  /** 覆盖默认体积上限（测试用）。 */
  maxBytes?: number;
  /** 覆盖写入基准时间（测试用，便于断言排序）。 */
  now?: number;
  presetId?: string;
  conversationId?: string;
  userId?: string;
}

export interface SaveGeneratedImagesResult {
  /** 实际写入的图库记录。 */
  saved: GalleryImage[];
  /** 因重复 dataUrl 跳过的张数（批内重复 + 已存在）。 */
  duplicates: number;
  /** 因超过体积上限跳过的张数。 */
  oversized: number;
  /** 需要提示用户的文案（存在跳过项时给出，否则为 undefined）。 */
  warning?: string;
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/** 字节数 → 便于阅读的中文大小文案。 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '未知大小';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${round1(kb)} KB`;
  return `${round1(kb / 1024)} MB`;
}

/**
 * 估算 dataUrl 的字节数：data URL 主体多为 base64 文本，按 3/4 还原为字节；
 * 非 base64（如 `data:image/svg+xml,...`）按 UTF-8 文本长度估算。
 */
export function estimateDataUrlBytes(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  const header = dataUrl.slice(0, comma < 0 ? 0 : comma);
  const body = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  if (header.includes(';base64')) {
    const padding = body.endsWith('==') ? 2 : body.endsWith('=') ? 1 : 0;
    return Math.max(0, Math.floor((body.length * 3) / 4) - padding);
  }
  return body.length;
}

/**
 * 批量保存生成图。
 *
 * 重复（批内或已入库）与超限图片会被跳过并计数，返回 `warning` 供页面提示；
 * 其余图片按出现顺序落库，createdAt 依次递增以保证排序稳定。
 */
export async function saveGeneratedImages(
  images: readonly GeneratedImage[],
  options: SaveGeneratedImagesOptions,
): Promise<SaveGeneratedImagesResult> {
  const maxBytes = options.maxBytes ?? DEFAULT_MAX_DATA_URL_BYTES;
  const baseTime = options.now ?? Date.now();
  const existing = await getDb().gallery.toArray();
  const seen = new Set(existing.filter(image => image.userId === options.userId &&
    (options.presetId !== undefined ? image.presetId === options.presetId
      : image.presetId === undefined && image.conversationId === options.conversationId))
    .map((image) => image.dataUrl));

  const saved: GalleryImage[] = [];
  let duplicates = 0;
  let oversized = 0;

  for (const image of images) {
    if (seen.has(image.dataUrl)) {
      duplicates += 1;
      continue;
    }
    if (estimateDataUrlBytes(image.dataUrl) > maxBytes) {
      oversized += 1;
      continue;
    }
    seen.add(image.dataUrl);
    const record = await createAndSaveGalleryImage({
      dataUrl: image.dataUrl,
      mime: image.mime,
      prompt: options.prompt,
      model: options.model,
      createdAt: baseTime + saved.length,
      ...(options.presetId === undefined ? {} : { presetId: options.presetId }),
      ...(options.conversationId === undefined ? {} : { conversationId: options.conversationId }),
      ...(options.userId === undefined ? {} : { userId: options.userId }),
      ...(image.revisedPrompt === undefined ? {} : { revisedPrompt: image.revisedPrompt }),
    });
    saved.push(record);
  }

  const parts: string[] = [];
  if (duplicates > 0) parts.push(`${duplicates} 张重复已跳过`);
  if (oversized > 0) parts.push(`${oversized} 张超过 ${formatBytes(maxBytes)} 未保存`);
  return {
    saved,
    duplicates,
    oversized,
    ...(parts.length === 0 ? {} : { warning: `${parts.join('，')}。` }),
  };
}
